import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ProductDetail, type StockProducts } from '../api';
import { StockStatusBadge } from '../components/StatusBadge';

function fmtQty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
function fmtDate(d: string | null): string {
  if (!d) return '—';
  const [y, m, j] = d.split('-');
  return y && m && j ? `${j}.${m}.${y}` : d;
}
function fmtMoney(n: number): string {
  return n.toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
const SENS: Record<string, string> = { in: 'Entrée', out: 'Sortie', interne: 'Interne' };

// Odoo's display_name is prefixed with "[code] " — the code already has its own
// column, so drop it to keep the Produit column narrow.
function stripCode(name: string, code: string): string {
  const prefix = `[${code}] `;
  return code && name.startsWith(prefix) ? name.slice(prefix.length) : name;
}

const FILTERS: [string, string][] = [
  ['all', 'Tout'],
  ['in_stock', 'En stock'],
  ['rupture', 'Ruptures'],
  ['below_min', 'Sous mini'],
  ['overstock', 'Surstock'],
  ['reserved', 'Réservés'],
];

export default function Stocks() {
  const [data, setData] = useState<StockProducts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ProductDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    if (openId == null) { setDetail(null); return; }
    setDetailLoading(true);
    setDetailError(null);
    api.getProduct(openId)
      .then(setDetail)
      .catch((e) => setDetailError(e.message))
      .finally(() => setDetailLoading(false));
  }, [openId]);

  function reload() {
    setLoading(true);
    setError(null);
    api.getStockProducts({ q, status })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }
  useEffect(() => { reload(); }, [status, q]);

  const c = data?.counts;

  return (
    <div className="devis-page">
      <div className="page-toolbar">
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>

      {c && (
        <div className="kpi-grid stock-kpis">
          <div className="kpi-card kpi-danger"><div className="kpi-label">Ruptures</div><div className="kpi-value">{c.rupture}</div></div>
          <div className="kpi-card"><div className="kpi-label">Sous mini</div><div className="kpi-value">{c.below_min}</div></div>
          <div className="kpi-card"><div className="kpi-label">Surstock</div><div className="kpi-value">{c.overstock}</div></div>
          <div className="kpi-card"><div className="kpi-label">Réservés</div><div className="kpi-value">{c.reserved}</div></div>
          <div className="kpi-card"><div className="kpi-label">Produits{c.capped ? ' (max)' : ''}</div><div className="kpi-value">{c.total}</div></div>
        </div>
      )}

      <div className="ventes-toolbar">
        <div className="suivi-filters">
          {FILTERS.map(([k, lbl]) => (
            <button key={k} className={`suivi-filter ${status === k ? 'active' : ''}`} onClick={() => setStatus(k)}>{lbl}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); }}>
          <input
            className="stock-search"
            placeholder="Rechercher (nom ou code)…"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
          />
        </form>
      </div>

      <div className="devis-list stock-list">
        {loading && <div className="devis-empty">Chargement…</div>}
        {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
        {!loading && !error && data && data.items.length === 0 && <div className="devis-empty">Aucun produit.</div>}
        {!loading && !error && data && data.items.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Code</th><th className="prod">Produit</th>
                <th className="num">Vendu</th>
                <th className="num">Physique</th><th className="num">Réservé</th><th className="num">Libre</th>
                <th className="num">Entrant</th><th className="num">Prév.</th>
                <th className="num">Min</th><th className="num">Max</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id} className={openId === p.id ? 'row-selected' : ''}>
                  <td className="mono">
                    <button
                      className="link-btn"
                      title="Voir la fiche produit et ses lots"
                      onClick={() => setOpenId(openId === p.id ? null : p.id)}
                    >
                      {p.code || `#${p.id}`}
                    </button>
                  </td>
                  <td className="prod"><span title={p.name}>{stripCode(p.name, p.code)}</span></td>
                  <td className="num">{p.sold ? fmtQty(p.sold) : '—'}</td>
                  <td className={`num ${p.on_hand <= 0 ? 'stock-short' : ''}`}>{fmtQty(p.on_hand)}</td>
                  <td className="num">{p.reserved ? fmtQty(p.reserved) : '—'}</td>
                  <td className={`num ${p.free < 0 ? 'stock-short' : ''}`}>{fmtQty(p.free)}</td>
                  <td className="num">{p.incoming ? fmtQty(p.incoming) : '—'}</td>
                  <td className="num">{fmtQty(p.forecast)}</td>
                  <td className="num">{p.min ? fmtQty(p.min) : '—'}</td>
                  <td className="num">{p.max ? fmtQty(p.max) : '—'}</td>
                  <td><StockStatusBadge status={p.status} /></td>
                </tr>
              )).flatMap((row, i) => {
                const p = data.items[i]!;
                if (openId !== p.id) return [row];
                return [row, (
                  <tr key={`${p.id}-detail`} className="detail-row">
                    <td colSpan={11}>
                      <ProductPanel
                        detail={detail}
                        detailLoading={detailLoading}
                        detailError={detailError}
                        onClose={() => setOpenId(null)}
                      />
                    </td>
                  </tr>
                )];
              })}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

/**
 * Fiche produit dépliée SOUS la ligne cliquée : la liste fait plusieurs centaines
 * de lignes, un panneau en pied de page serait hors écran.
 */
function ProductPanel({
  detail, detailLoading, detailError, onClose,
}: {
  detail: ProductDetail | null;
  detailLoading: boolean;
  detailError: string | null;
  onClose: () => void;
}) {
  return (
    <>
      {detailLoading && <div className="devis-empty">Chargement de la fiche…</div>}
          {detailError && !detailLoading && <div className="devis-empty err">Erreur : {detailError}</div>}
          {detail && !detailLoading && (() => {
            const d = detail.product;
            return (
              <div style={{ padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0 }}>
                    <span className="mono">{d.code || `#${d.id}`}</span> — {stripCode(d.name, d.code)}
                  </h3>
                  <StockStatusBadge status={d.status} />
                  {!d.active && <span className="badge">archivé</span>}
                  <button className="devis-refresh" style={{ marginLeft: 'auto' }} onClick={onClose}>Fermer</button>
                </div>

                <div className="kpi-grid stock-kpis" style={{ marginTop: 14 }}>
                  <div className="kpi-card"><div className="kpi-label">Physique</div><div className="kpi-value">{fmtQty(d.on_hand)}</div></div>
                  <div className="kpi-card"><div className="kpi-label">Réservé</div><div className="kpi-value">{fmtQty(d.reserved)}</div></div>
                  <div className="kpi-card"><div className="kpi-label">Libre</div><div className="kpi-value">{fmtQty(d.free)}</div></div>
                  <div className="kpi-card"><div className="kpi-label">Entrant</div><div className="kpi-value">{fmtQty(d.incoming)}</div></div>
                  <div className="kpi-card"><div className="kpi-label">Prévisionnel</div><div className="kpi-value">{fmtQty(d.forecast)}</div></div>
                </div>

                <table style={{ marginTop: 14 }}>
                  <tbody>
                    <tr><th>Catégorie</th><td>{d.category ?? '—'}</td><th>Unité</th><td>{d.uom ?? '—'}</td></tr>
                    <tr><th>Code-barres</th><td className="mono">{d.barcode ?? '—'}</td><th>Seuils min / max</th><td>{d.min ? fmtQty(d.min) : '—'} / {d.max ? fmtQty(d.max) : '—'}</td></tr>
                    <tr><th>Prix de vente</th><td>{fmtMoney(d.sale_price)}</td><th>Coût</th><td>{d.cost_price ? fmtMoney(d.cost_price) : '—'}</td></tr>
                    <tr>
                      <th>Suivi</th>
                      <td>{d.tracking === 'lot' ? 'par lot' : d.tracking === 'serial' ? 'par n° de série' : 'aucun'}</td>
                      <th>Péremption</th>
                      <td>{d.use_expiration_date ? `oui${d.expiration_time ? ` (${d.expiration_time} j)` : ''}` : 'non gérée'}</td>
                    </tr>
                  </tbody>
                </table>

                <h4 style={{ margin: '18px 0 6px' }}>Lots en stock</h4>
                {d.tracking === 'none' ? (
                  <div style={{ opacity: .7 }}>
                    Produit non suivi par lot : Odoo n'enregistre aucun lot pour cette référence, même si
                    le fournisseur en indique un. Activer le suivi pour la traçabilité de rappel.
                  </div>
                ) : detail.lots.length === 0 ? (
                  <div style={{ opacity: .7 }}>Aucun lot en stock actuellement.</div>
                ) : (
                  <table>
                    <thead><tr><th>Lot</th><th className="num">Quantité</th><th className="num">Réservé</th><th>Péremption</th><th></th></tr></thead>
                    <tbody>
                      {detail.lots.map((l) => (
                        <tr key={l.id}>
                          <td className="mono"><b>{l.name}</b></td>
                          <td className="num">{fmtQty(l.qty)}</td>
                          <td className="num">{l.reserved ? fmtQty(l.reserved) : '—'}</td>
                          <td>
                            {fmtDate(l.expiration)}
                            {l.expired && <span className="badge badge-danger" style={{ marginLeft: 6 }}>périmé</span>}
                          </td>
                          <td><Link className="link-btn" to={`/lots?q=${encodeURIComponent(l.name)}`}>Traçabilité →</Link></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <h4 style={{ margin: '18px 0 6px' }}>Stock par emplacement</h4>
                {detail.stock.length === 0 ? (
                  <div style={{ opacity: .7 }}>Aucun stock.</div>
                ) : (
                  <table>
                    <thead><tr><th>Emplacement</th><th>Lot</th><th className="num">Quantité</th><th className="num">Réservé</th></tr></thead>
                    <tbody>
                      {detail.stock.map((q2, i) => (
                        <tr key={i}>
                          <td>{q2.location}</td>
                          <td className="mono">{q2.lot ?? '—'}</td>
                          <td className="num">{fmtQty(q2.qty)}</td>
                          <td className="num">{q2.reserved ? fmtQty(q2.reserved) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <h4 style={{ margin: '18px 0 6px' }}>Derniers mouvements</h4>
                {detail.movements.length === 0 ? (
                  <div style={{ opacity: .7 }}>Aucun mouvement validé.</div>
                ) : (
                  <table>
                    <thead><tr><th>Date</th><th>Sens</th><th className="num">Qté</th><th>Lot</th><th>Document</th><th>Partenaire</th><th>Origine</th></tr></thead>
                    <tbody>
                      {detail.movements.map((mv, i) => (
                        <tr key={i}>
                          <td>{fmtDate(mv.date)}</td>
                          <td>{SENS[mv.sens] ?? mv.sens}</td>
                          <td className="num">{fmtQty(mv.qty)}</td>
                          <td className="mono">
                            {mv.lot
                              ? <Link className="link-btn" to={`/lots?q=${encodeURIComponent(mv.lot)}`}>{mv.lot}</Link>
                              : '—'}
                          </td>
                          <td className="mono">{mv.picking ?? '—'}</td>
                          <td>{mv.partner ?? '—'}</td>
                          <td>{mv.origin ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })()}
    </>
  );
}
